import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { collection, addDoc, query, where, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Calendar, Clock, Users, MapPin, Phone, User, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export function ReservationModal({ game, isOpen, onClose }) {
  const { user, userData } = useAuth();
  const [branches, setBranches] = useState([]);
  const [existingReservations, setExistingReservations] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    branchId: '',
    date: '',
    time: '',
    playerCount: '',
    name: userData?.name || '',
    phone: userData?.phone || ''
  });

  // Player count options
  const playerOptions = ['2', '3', '4', '5', '6'];

  useEffect(() => {
    if (isOpen && game) {
      fetchBranches();
      setFormData(prev => ({
        ...prev,
        name: userData?.name || '',
        phone: userData?.phone || ''
      }));
    }
  }, [isOpen, game, userData]);

  useEffect(() => {
    if (formData.branchId) {
      const branch = branches.find(b => b.id === formData.branchId);
      setSelectedBranch(branch);
    }
  }, [formData.branchId, branches]);

  // Real-time listener for reservations when date and branch are selected
  useEffect(() => {
    if (!game || !formData.branchId || !formData.date) {
      setExistingReservations([]);
      return;
    }

    const q = query(
      collection(db, 'reservations'),
      where('gameId', '==', game.id),
      where('branchId', '==', formData.branchId),
      where('date', '==', formData.date),
      where('status', 'in', ['pending', 'confirmed'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => a.time.localeCompare(b.time));
      setExistingReservations(data);
    });

    return () => unsubscribe();
  }, [game, formData.branchId, formData.date]);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const gameBranchIds = game.branches || [];
      if (gameBranchIds.length === 0) {
        setBranches([]);
        return;
      }

      const branchesData = [];
      for (const branchId of gameBranchIds) {
        const branchDoc = await getDoc(doc(db, 'branches', branchId));
        if (branchDoc.exists()) {
          branchesData.push({ id: branchDoc.id, ...branchDoc.data() });
        }
      }
      setBranches(branchesData);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse working hours from branch (accepts 00:00 as end-of-day = 24:00)
  const parseWorkingHours = (workingHours) => {
    if (!workingHours) return { start: '10:00', end: '23:00' };
    const match = workingHours.match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
    if (match) {
      return { start: match[1], end: match[2] };
    }
    return { start: '10:00', end: '23:00' };
  };

  const toMinutes = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const fromMinutes = (min) => {
    const h = Math.floor(min / 60) % 24;
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Build available slots (60-min interval) within branch's working hours
  const availableTimeSlots = (() => {
    if (!selectedBranch) return [];
    const { start, end } = parseWorkingHours(selectedBranch.workingHours);
    let startMin = toMinutes(start);
    let endMin = toMinutes(end);
    // If end is "00:00" treat it as 24:00 (end of day)
    if (endMin === 0) endMin = 24 * 60;
    // Handle wrap-around (e.g. 12:00 - 02:00)
    if (endMin <= startMin) endMin += 24 * 60;
    const slots = [];
    for (let m = startMin; m < endMin; m += 60) {
      slots.push(fromMinutes(m));
    }
    return slots;
  })();

  const isTimeInWorkingHours = (time) => availableTimeSlots.includes(time);

  const isTimeSlotTaken = (time) => {
    return existingReservations.some(res => res.time === time);
  };

  const validateTime = (time) => {
    if (!time) return { valid: false, error: 'Saat seçin' };
    if (!isTimeInWorkingHours(time)) {
      const { start, end } = parseWorkingHours(selectedBranch?.workingHours);
      return { valid: false, error: `İş saatları: ${start} - ${end}` };
    }
    if (isTimeSlotTaken(time)) {
      return { valid: false, error: 'Bu saat artıq rezerv edilib' };
    }
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!user) {
      toast.error('Rezervasiya üçün daxil olmalısınız');
      return;
    }

    if (!formData.branchId || !formData.date || !formData.time || !formData.playerCount || !formData.name || !formData.phone) {
      toast.error('Bütün sahələri doldurun');
      return;
    }

    const timeValidation = validateTime(formData.time);
    if (!timeValidation.valid) {
      toast.error(timeValidation.error);
      return;
    }

    setSubmitting(true);
    try {
      const selectedBranchData = branches.find(b => b.id === formData.branchId);
      
      const reservationData = {
        gameId: game.id,
        gameName: game.name,
        branchId: formData.branchId,
        branchName: selectedBranchData?.name || '',
        userId: user.uid,
        userEmail: user.email,
        customerName: formData.name,
        customerPhone: formData.phone,
        date: formData.date,
        time: formData.time,
        playerCount: parseInt(formData.playerCount),
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'reservations'), reservationData);
      
      toast.success('Rezervasiya sorğusu göndərildi! Admin təsdiqlədikdən sonra aktiv olacaq.');
      onClose();
      
      setFormData({
        branchId: '',
        date: '',
        time: '',
        playerCount: '',
        name: userData?.name || '',
        phone: userData?.phone || ''
      });
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error('Xəta baş verdi');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  if (!game) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#333] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-red-500" />
            Rezervasiya - {game.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full" />
          </div>
        ) : branches.length === 0 ? (
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Bu oyun heç bir filialda mövcud deyil</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Branch Selection */}
            <div>
              <Label className="text-gray-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Filial seçin *
              </Label>
              <Select
                value={formData.branchId}
                onValueChange={(value) => setFormData({ ...formData, branchId: value, time: '' })}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue placeholder="Filial seçin" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#333] text-white">
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name} - {branch.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedBranch?.workingHours && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  İş saatları: {selectedBranch.workingHours}
                </p>
              )}
            </div>

            {/* Date Selection */}
            <div>
              <Label className="text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Tarix seçin *
              </Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value, time: '' })}
                min={today}
                max={maxDateStr}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>

            {/* Show existing reservations for selected date */}
            {formData.branchId && formData.date && existingReservations.length > 0 && (
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-gray-400 text-sm mb-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Bu tarixdə dolu saatlar:
                </p>
                <div className="flex flex-wrap gap-2">
                  {existingReservations.map((res) => (
                    <span
                      key={res.id}
                      className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                        res.status === 'confirmed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {res.status === 'confirmed' ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      {res.time} ({res.playerCount} nəfər)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Time Picker - grid of available slots within branch working hours */}
            {formData.branchId && formData.date && (
              <div>
                <Label className="text-gray-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Saatı seçin *
                </Label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2" data-testid="time-slots-grid">
                  {availableTimeSlots.length === 0 && (
                    <p className="col-span-full text-gray-500 text-xs">
                      Filialın iş saatları müəyyən edilməyib.
                    </p>
                  )}
                  {availableTimeSlots.map((slot) => {
                    const taken = isTimeSlotTaken(slot);
                    const selected = formData.time === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={taken}
                        onClick={() => setFormData({ ...formData, time: slot })}
                        data-testid={`time-slot-${slot}`}
                        className={`py-2 rounded-lg text-sm font-mono border transition-colors ${
                          selected
                            ? 'bg-[#D90429] border-[#D90429] text-white'
                            : taken
                              ? 'bg-black/40 border-red-900/40 text-red-400/40 line-through cursor-not-allowed'
                              : 'bg-[#0A0A0A] border-white/10 text-white hover:border-[#D90429]/60'
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
                {formData.time && !validateTime(formData.time).valid && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    {validateTime(formData.time).error}
                  </p>
                )}
                {formData.time && validateTime(formData.time).valid && (
                  <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Bu saat boşdur — {formData.time}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Qırmızı və üstündən xətt çəkilmiş saatlar artıq rezerv edilib.
                </p>
              </div>
            )}

            {/* Player Count */}
            <div>
              <Label className="text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Neçə nəfər? *
              </Label>
              <Select
                value={formData.playerCount}
                onValueChange={(value) => setFormData({ ...formData, playerCount: value })}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue placeholder="Nəfər sayı" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#333] text-white">
                  {playerOptions.map((count) => (
                    <SelectItem key={count} value={count}>
                      {count} nəfər
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Ad Soyad *
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="Ad Soyad"
                />
              </div>
              <div>
                <Label className="text-gray-400 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Mobil *
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="+994 50 123 45 67"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-white/20"
                onClick={onClose}
                disabled={submitting}
              >
                Ləğv Et
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={submitting}
              >
                {submitting ? 'Göndərilir...' : 'Rezerv Et'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReservationModal;
